import React, { useState, useEffect } from "react";
import { Flex, Heading, Text, Box, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import FormControlDefault from "../../../../../components/input/FormControlDefault";
import { textSecondary } from "../../../../../utils/colors";
import FormControlBank from "../../../../../components/input/FormControlBank";
import { updateTenantCompanyDetailsAction } from "../../../../../actions/tenantActions";
import { useDispatch, useSelector } from "react-redux";
import { TENANT_UPDATE_COMPANY_DETAILS_RESET } from "../../../../../constants/tenantConstants";
import SaveButtons from "./SaveButtons";
import { useParams } from "react-router-dom";

const ClinicCompanyDetails = ({ slug, tenant }) => {
  // GENERAL
  // const { slug } = useParams();
  const { t } = useTranslation();
  const toast = useToast();

  // COLORS
  const { whiteMode, backgroundLightMode, textPrimaryMode, newBorderMode } =
    useThemeColors();

  // STATE
  const [showButtons, setShowButtons] = useState(false);

  const [companyName, setCompanyName] = useState(tenant?.companyName);
  const [companyCode, setCompanyCode] = useState(tenant?.companyCode);
  const [vatCode, setVatCode] = useState(tenant?.vatCode);
  const [iban, setIban] = useState(tenant?.iban);
  const [bankName, setBankName] = useState(tenant?.bankName);

  // REDUX
  const dispatch = useDispatch();

  const tenantUpdateCompanyDetails = useSelector(
    (state) => state.tenantUpdateCompanyDetails
  );

  const {
    loading: loadingUpdateCompanyDetails,
    error: errorUpdateCompanyDetails,
    success: successUpdateCompanyDetails,
  } = tenantUpdateCompanyDetails;

  // HANDLERS
  const updateCompanyDetailsHandler = () => {
    const companyDetails = {
      companyName,
      companyCode,
      vatCode,
      iban,
      bankName,
    };

    dispatch(
      updateTenantCompanyDetailsAction(slug, tenant.tenantID, companyDetails)
    );
  };

  const normalizeValue = (value) =>
    value === undefined || value === null ? "" : String(value).trim();

  // USE EFFECT
  useEffect(() => {
    // ✅ Check if any value has changed
    const hasChanges =
      normalizeValue(companyName) !== normalizeValue(tenant?.companyName) ||
      normalizeValue(companyCode) !== normalizeValue(tenant?.companyCode) ||
      normalizeValue(vatCode) !== normalizeValue(tenant?.vatCode) ||
      normalizeValue(iban) !== normalizeValue(tenant?.iban) ||
      normalizeValue(bankName) !== normalizeValue(tenant?.bankName);

    if (hasChanges) {
      setShowButtons(true);
    } else {
      setShowButtons(false);
    }
  }, [companyName, companyCode, vatCode, iban, bankName, tenant]);

  useEffect(() => {
    if (successUpdateCompanyDetails) {
      toast({
        title: t("toasts.clinicDetailsUpdated"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowButtons(false);
    } else if (errorUpdateCompanyDetails) {
      toast({
        title: t("toasts.clinicDetailsUpdatedError"),
        status: "error",
        duration: 3000,

        isClosable: true,
      });
    }

    dispatch({ type: TENANT_UPDATE_COMPANY_DETAILS_RESET });
  }, [successUpdateCompanyDetails, errorUpdateCompanyDetails]);

  // LOGS
  // console.log("tenant", tenant);
  // console.log("companyName", companyName);
  // console.log("companyCode", companyCode);
  // console.log("vatCode", vatCode);
  // console.log("iban", iban);
  // console.log("bankName", bankName);

  return (
    <Box
      w="100%"
      p={["1.5rem 0", "3rem 0rem 3rem"]}
      borderBottom={`1px solid ${newBorderMode}`}
    >
      <Flex
        w="100%"
        justify="space-between"
        gap={["1.5rem", "2rem"]}
        direction={["column", "row"]}
      >
        {/* LEFT START */}
        <Flex direction="column" gap={2} w={["100%", "40%"]}>
          <Heading fontSize="1rem" fontWeight="600" p={["0 1.5rem", "initial"]}>
            {t("clinicSettings.companyDetails")}
          </Heading>
          <Text
            color={textSecondary}
            fontSize="sm"
            fontWeight="500"
            p={["0 1.5rem", "initial"]}
          >
            {t("clinicSettings.companyDetailsDescription")}
          </Text>
        </Flex>
        {/* LEFT END */}

        {/* RIGHT START */}
        <Flex
          w={["100%", "800px"]}
          direction="column"
          justify="flex-start"
          align="flex-start"
        >
          <Flex
            direction="column"
            w="100%"
            bg={backgroundLightMode}
            borderRadius="1rem"
            p="1.25rem 1.5rem 0.25rem"
          >
            <Flex
              gap={["0rem", "2rem", "2rem"]}
              w="100%"
              direction={["column", "row", "row"]}
            >
              <FormControlDefault
                //   autoFocus
                type="text"
                size="lg"
                labelFontSize="0.8rem"
                isRequired
                label={t("form.companyName")}
                value={companyName}
                placeholder={t("placeholder.companyName")}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                }}
                w={["100%", "100%", "100%"]}
              />
            </Flex>

            <Flex
              gap={["0rem", "2rem", "2rem"]}
              w="100%"
              direction={["column", "row", "row"]}
            >
              <FormControlDefault
                type="text"
                size="lg"
                labelFontSize="0.8rem"
                isRequired
                label={t("form.companyCode")}
                value={companyCode}
                placeholder={t("placeholder.companyCode")}
                onChange={(e) => {
                  setCompanyCode(e.target.value);
                }}
                w={["100%", "50%", "50%"]}
              />

              <FormControlDefault
                type="text"
                size="lg"
                labelFontSize="0.8rem"
                label={t("form.vatCode")}
                value={vatCode}
                placeholder={t("placeholder.vatCode")}
                onChange={(e) => {
                  setVatCode(e.target.value);
                }}
                w={["100%", "50%", "50%"]}
              />
            </Flex>

            <Flex
              gap={["0rem", "2rem", "2rem"]}
              w="100%"
              direction={["column", "row", "row"]}
            >
              <FormControlDefault
                type="text"
                size="lg"
                label={t("form.iban")}
                labelFontSize="0.8rem"
                value={iban}
                placeholder={t("placeholder.iban")}
                onChange={(e) => {
                  setIban(e.target.value);
                }}
                w="50%"
              />

              <FormControlDefault
                type="text"
                size="lg"
                label={t("form.bankName")}
                labelFontSize="0.8rem"
                value={bankName}
                placeholder={t("placeholder.bankName")}
                onChange={(e) => {
                  setBankName(e.target.value);
                }}
                w="50%"
              />

              {/* <FormControlBank
                isSearchable={true}
                label={t("form.bankName")}
                size="lg"
                labelFontSize="0.8rem"
                value={bankName}
                placeholder={t("placeholder.bankName")}
                onChange={(selectedOption) => {
                  setBankName(selectedOption.value);
                }}
                w="50%"
              /> */}
            </Flex>
          </Flex>
        </Flex>
        {/* RIGHT END */}
      </Flex>

      {/* BUTTONS */}
      {showButtons && (
        <SaveButtons
          onClose={() => {
            setShowButtons(false);
            setCompanyName(tenant.companyName);
            setCompanyCode(tenant.companyCode);
            setVatCode(tenant.vatCode);
            setIban(tenant.iban);
            setBankName(tenant.bankName);
          }}
          onClick={updateCompanyDetailsHandler}
          loading
        />
      )}
    </Box>
  );
};

export default ClinicCompanyDetails;
